// /police-clearance-foreigner/$nationality/for/$visa — TH combo (Nationality × Thai Visa)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { FOREIGNER_NATIONALITIES, getForeignerNationality } from "@/data/police-foreigner-nationalities";
import { FOREIGNER_VISA_TYPES, getForeignerVisa } from "@/data/foreigner-visa-types";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { ShieldCheck, MessageCircle, Phone, BadgeCheck, ListChecks, AlertTriangle, FileCheck2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/police-clearance-foreigner/$nationality/for/$visa")({
  loader: ({ params }) => {
    if (!getForeignerNationality(params.nationality)) throw notFound();
    if (!getForeignerVisa(params.visa)) throw notFound();
    return { nSlug: params.nationality, vSlug: params.visa };
  },
  head: ({ params }) => {
    const n = getForeignerNationality(params.nationality);
    const v = getForeignerVisa(params.visa);
    if (!n || !v) return { meta: [{ title: ctaTitle("Not found", "/police-clearance-foreigner/$nationality/for/$visa", "th")}] };
    const url = `${SITE}/police-clearance-foreigner/${n.slug}/for/${v.slug}`;
    const title = `${n.homeAuthority} สำหรับ ${v.nameTh} — ชาว${n.nameTh} เริ่ม ฿${n.priceFromTh.toLocaleString()} | NYC`;
    const desc = `${v.taglineTh} · ${v.timelineTh} · โดยทนายสภาทนายความ — ส่งภาพเอกสารให้เราตรวจก่อน ฟรี`;
    return {
      meta: [
        { title: ctaTitle(title, "/police-clearance-foreigner/$nationality/for/$visa", "th") }, { name: "description", content: ctaDesc(desc, "/police-clearance-foreigner/$nationality/for/$visa", "th")},
        { property: "og:title", content: ctaTitle(title, "/police-clearance-foreigner/$nationality/for/$visa", "th")},
        { property: "og:description", content: ctaDesc(desc, "/police-clearance-foreigner/$nationality/for/$visa", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/police-clearance-foreigner/${n.slug}/for/${v.slug}`, `/en/police-clearance-foreigner/${n.slug}/for/${v.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${n.homeAuthorityEn} for ${v.nameEn} — ${n.nameEn} nationals`,
              serviceType: "Foreign PC + Thai Visa Support",
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: n.countryEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: n.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: `${SITE}/` },
                { "@type": "ListItem", position: 2, name: "Police Clearance", item: `${SITE}/police-clearance` },
                { "@type": "ListItem", position: 3, name: "ชาวต่างชาติในไทย", item: `${SITE}/police-clearance-foreigner` },
                { "@type": "ListItem", position: 4, name: `ชาว${n.nameTh}`, item: `${SITE}/police-clearance-foreigner/${n.slug}` },
                { "@type": "ListItem", position: 5, name: v.nameTh, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: ComboPage,
});

function ComboPage() {
  const { nSlug, vSlug } = Route.useLoaderData() as { nSlug: string; vSlug: string };
  const n = getForeignerNationality(nSlug)!;
  const v = getForeignerVisa(vSlug)!;
  const otherVisas = FOREIGNER_VISA_TYPES.filter((x) => x.slug !== vSlug);
  const otherNats = FOREIGNER_NATIONALITIES.filter((x) => x.slug !== nSlug).slice(0, 8);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/police-clearance-foreigner" className="hover:text-gold">ชาวต่างชาติในไทย</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/police-clearance-foreigner/$nationality" params={{ nationality: nSlug }} className="hover:text-gold">ชาว{n.nameTh}</Link><span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{v.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> {v.codeTh} · ชาว{n.nameTh}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            {n.homeAuthority} สำหรับ {v.nameTh} — ชาว{n.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            ชุดเอกสารครบสำหรับยื่น {v.nameTh} ({v.codeTh}) — {v.taglineTh} · เริ่ม ฿{n.priceFromTh.toLocaleString()} · รวม {v.timelineTh}
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NYC Legal & Notary Service — {n.homeAuthority} + {n.hague ? "Apostille" : "Legalization"} + แปลไทยรับรอง MFA
          สำหรับยื่น {v.nameTh} ({v.codeTh}) ที่ตม.ไทย/อำเภอ/BOI · ระยะเวลารวม {v.timelineTh} · เริ่ม ฿{n.priceFromTh.toLocaleString()}
        </blockquote>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <FileCheck2 className="h-6 w-6 text-amber-600" /> เอกสารที่ตม./อำเภอ/BOI ต้องการ
          </h2>
          <ul className="mt-5 space-y-2">
            {v.keyEvidenceTh.map((e, i) => (
              <li key={i} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800">✓ {e}</li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <ListChecks className="h-6 w-6 text-amber-600" /> ขั้นตอนบริการ NYC
          </h2>
          <ol className="mt-5 space-y-3 text-[15px]">
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">1. ประเมินฟรี:</strong> ส่ง Passport + วีซ่าปัจจุบันมาที่ LINE @nycli เพื่อประเมินเอกสารที่ต้องขอเพิ่ม</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">2. ขอ {n.homeAuthority}:</strong> ยื่นผ่านช่องทาง Official ของ {n.countryTh} ({n.homeAuthorityEn})</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">3. {n.hague ? "Apostille" : "Legalization"}:</strong> {n.hague ? "ปิด Apostille ที่หน่วยงานต้นทาง" : `Notary → Foreign Ministry ${n.countryTh} → ${n.embassyBkkTh}`}</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">4. แปลไทย + รับรอง MFA:</strong> แปลโดยนักแปลขึ้นทะเบียนกรมการกงสุล + รับรองที่กรมการกงสุล ถ.แจ้งวัฒนะ</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">5. ประสานเอกสารอื่นๆ:</strong> รวมกับ {v.imm} + หลักฐานอื่นๆ ที่ตม./อำเภอ/BOI ต้องการ</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">6. ยื่น + ติดตาม:</strong> ทีม NYC ยื่นและติดตามผลจนได้รับ {v.codeTh}</li>
          </ol>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <AlertTriangle className="h-6 w-6 text-rose-600" /> ข้อพลาดที่พบบ่อย ({v.codeTh})
          </h2>
          <ul className="mt-5 space-y-2">
            {v.pitfallsTh.map((p, i) => (
              <li key={i} className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4 text-[14px] text-slate-800">⚠️ {p}</li>
            ))}
          </ul>
        </section>

        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `ต้องใช้ PC บ้านเกิดสำหรับ ${v.nameTh} หรือไม่?`, a: v.requiresPc === "always" ? "จำเป็นเสมอ" : v.requiresPc === "usually" ? "ส่วนใหญ่ต้องใช้ (ขึ้นกับสำนักงานตม.)" : "บาง case เท่านั้น" },
                { q: `ราคาเริ่มต้นเท่าไร?`, a: `ชุด PC บ้านเกิด + Apostille/Legalization + แปลไทย MFA เริ่ม ฿${n.priceFromTh.toLocaleString()}` },
                { q: `ใช้เวลารวมกี่วัน?`, a: `${v.timelineTh} (รวมทุกขั้นตอน)` },
                { q: `ชาว${n.nameTh}ต้องบินกลับ${n.countryTh}ไหม?`, a: `ไม่จำเป็น — NYC ประสานผ่าน ${n.homeAuthorityEn} หรือ ${n.embassyBkkTh} ให้ได้ในหลายกรณี` },
                { q: `PC อายุใช้ได้กี่เดือน?`, a: `ตม.ไทย/อำเภอ/BOI รับ PC อายุไม่เกิน 3-6 เดือน (แต่ละหน่วยงานต่างกัน)` },
              ]}
            />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">วีซ่าอื่นๆ (ชาว{n.nameTh})</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-2">
            {otherVisas.map((x) => (
              <Link key={x.slug} to="/police-clearance-foreigner/$nationality/for/$visa"
                params={{ nationality: nSlug, visa: x.slug }}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 hover:border-gold/50">
                <span className="font-semibold">{x.nameTh}</span>
                <span className="ml-auto text-[10px] text-slate-500">{x.codeTh}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-xl font-extrabold text-navy-deep">{v.nameTh} · สัญชาติอื่น</h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherNats.map((r) => (
              <Link key={r.slug} to="/police-clearance-foreigner/$nationality/for/$visa"
                params={{ nationality: r.slug, visa: vSlug }}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 hover:border-gold/50">
                <span className="font-semibold">ชาว{r.nameTh}</span>
                <span className="ml-auto text-[10px] text-slate-500">฿{r.priceFromTh.toLocaleString()}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
      <QuotableStatsStrip cluster="police" locale="th" variant="light" />
    </main>
  );
}
